import type { RefObject } from 'react';
import type { AnimationAction, AnimationClip, AnimationMixer } from 'three';

import type { FrameRate } from '@cartwhl/common/types';

type Duration = {
  seconds: number;
  frames: number;
};

export type FrameMixerController = {
  update: (delta: number) => void;
  play: (isPlaying: boolean) => void;
  setFrame: (newFrame: number) => void;
  getFrame: () => number;
  getPlayStatus: () => boolean;
};

type CreateFrameMixerControllerArgs = {
  clip?: AnimationClip;
  mixerRef: RefObject<AnimationMixer>;
  activeActionRef: RefObject<AnimationAction | null>;
  frameRate: FrameRate;
  playbackSpeed: number;
  frameStepSize: number;
  loop: boolean;
  disabled: boolean;
  /** When true, PlayerStore owns frame advancement and this controller only seeks. */
  sharedTransport?: boolean;
  durationRef: RefObject<Duration>;
  onFrameUpdate?: (frame: number) => void;
  onPlayChange?: (isPlaying: boolean) => void;
};

export function createFrameMixerController({
  clip,
  mixerRef,
  activeActionRef,
  frameRate,
  playbackSpeed,
  frameStepSize,
  loop,
  disabled,
  sharedTransport = false,
  durationRef,
  onFrameUpdate,
  onPlayChange,
}: CreateFrameMixerControllerArgs): FrameMixerController {
  const frameTime = 1 / frameRate;
  let accumulator = 0;

  const getPlaybackTime = () => {
    const activeAction = activeActionRef.current;
    if (activeAction) {
      return activeAction.time;
    }

    const mixer = mixerRef.current;
    return mixer ? mixer.time : 0;
  };

  const getPlayStatus = () => mixerRef.current.timeScale !== 0;

  const getFrame = () => {
    const mixer = mixerRef.current;
    if (!mixer) return 0;

    const durationSeconds = durationRef.current.seconds;
    const durationFrames = durationRef.current.frames;
    if (durationSeconds <= 0 || durationFrames <= 0) return 0;

    const currentFrame = Math.round(getPlaybackTime() / frameTime);
    if (loop) {
      return currentFrame % durationFrames;
    }

    return Math.min(currentFrame, durationFrames - 1);
  };

  const update = (delta: number) => {
    if (disabled || sharedTransport) return;

    const mixer = mixerRef.current;
    accumulator += delta * playbackSpeed;

    while (accumulator >= frameTime * frameStepSize) {
      mixer.update(frameTime * frameStepSize);
      accumulator -= frameTime * frameStepSize;
    }

    const durationFrames = durationRef.current.frames;
    const currentFrame = Math.round(getPlaybackTime() / frameTime);
    if (!loop && durationFrames > 0 && currentFrame >= durationFrames) {
      const endFrame = Math.max(durationFrames - 1, 0);
      const endTime = endFrame * frameTime;

      mixer.setTime(endTime);
      mixer.timeScale = 0;
      accumulator = 0;
      onFrameUpdate?.(endFrame);
      onPlayChange?.(false);
      return;
    }

    if (onFrameUpdate && mixer.timeScale > 0) {
      onFrameUpdate(getFrame());
    }
  };

  const play = (isPlaying: boolean) => {
    if (disabled) return;
    const mixer = mixerRef.current;
    mixer.timeScale = isPlaying ? 1 : 0;
    onPlayChange?.(isPlaying);
  };

  const setFrame = (newFrame: number) => {
    if (!clip) return;

    const mixer = mixerRef.current;
    if (!mixer) return;

    const durationFrames = durationRef.current.frames;
    if (durationFrames <= 0) return;

    const frame = sharedTransport
      ? Math.max(0, Math.min(newFrame, durationFrames - 1))
      : loop
        ? ((newFrame % durationFrames) + durationFrames) % durationFrames
        : Math.max(0, Math.min(newFrame, durationFrames - 1));
    const steppedFrame = Math.floor(frame / frameStepSize) * frameStepSize;

    const action = mixer.clipAction(clip);
    const time = steppedFrame * frameTime;
    if (!Number.isFinite(time)) return;
    const originalTimeScale = mixer.timeScale;

    mixer.timeScale = 1;
    action.time = time;
    mixer.setTime(time);
    mixer.timeScale = originalTimeScale;

    if (!sharedTransport) {
      onFrameUpdate?.(getFrame());
    }
  };

  return {
    update,
    play,
    setFrame,
    getFrame,
    getPlayStatus,
  };
}
